import React, { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import { Grid, GridCol, GridRow } from '@naf/grid';
import { TextVariant } from '@naf/text';
import StepWizard from 'react-step-wizard';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CustomerMembershipStatus } from '@dtp/membership-service-types';
import { LayoutWithMainContent } from '@components/layout/Layout';
import S from '@styles/becomeAMember/StyledBecomeAMember';
import { actions as becomeAMemberActions } from '@redux/modules/becomeAMember';
import { useQueryParams } from '@hooks/useQueryParameters';
import useSelector from '@redux/typedHooks';
import { useFetchProfile } from '@hooks/useFetchProfile';
import { useAuth0Token } from '@hooks/useAuth0Token';
import { actions } from '@redux/modules/myMembership';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import { getPageReferrer, setLastVirtualPageUrl } from '@hooks/VirtualPageViewTracking/pageReferrer';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { MEMBERPAGES_URL } from '../../lib/constants';
import { getURLParams } from '../../lib/getUrlParams';
import { updateUrlQueryValues } from '../../utils/updateUrlQueryValues';
import { Redirect } from './forms/Redirect';
import { Progressbar } from './components/ProgressBar';
import { LoaderContent } from '../LoaderPage';
import { Addons } from './forms/Addons';
import { useInitializeMembershipProduct, useProduct } from './BecomeAMember.utils';
import { ActiveStep, Step } from './BecomeAMember.types';
import {
  clearVippsMembershipRegistrationMethod,
  hasChosenWithoutVipps,
  hasStartedVippsMembership,
} from './components/VippsModal/VippsModal.utils';

const Members = loadable(() => import('./forms/Members'), { resolveComponent: (m) => m.Members });

const Consents = loadable(() => import('./forms/Consents'), { resolveComponent: (m) => m.Consents });

const documentTitle = (step: number | undefined, steps?: Step[]) => {
  const stepName = step && step > 1 ? steps?.[step - 1]?.name : undefined;
  if (stepName) return `Bli Medlem - ${stepName}`;
  return 'Bli Medlem';
};

export const BecomeAMember = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const windowPath = useWindowLocation();
  const dataLayer = useGTMDataLayer();
  const { richToken } = useAuth0Token();
  const queryParams = useQueryParams();
  const { profile } = useFetchProfile();
  const [profileIsLoaded, setProfileIsLoaded] = useState(false);
  const { product } = useProduct();
  const hasInitializedMembershipProduct = useInitializeMembershipProduct();
  const baseUrl = useSelector((state) => state.application.baseUrl);
  const becomeAMemberData = useSelector((state) => state.becomeAMember.data);
  const hasBecomeAMemberProducts = Object.keys(becomeAMemberData.products || {}).length > 0;
  const { campaignCodeDetails } = becomeAMemberData;

  const [initialWizardStep, setInitialWizardStep] = useState<number | null>(null);
  useEffect(() => {
    if (!hasStartedVippsMembership() && !hasChosenWithoutVipps()) {
      clearVippsMembershipRegistrationMethod();
    }

    const params = getURLParams();
    const parsed = params?.wizardStep && parseInt(params.wizardStep, 10);
    setInitialWizardStep(parsed && parsed > 0 ? parsed : 1);
  }, []);

  useEffect(() => {
    if (typeof queryParams === 'undefined') {
      return;
    }

    const { campaignCode } = queryParams;
    const needsDataFetch = !hasBecomeAMemberProducts;
    const campaignCodeChanged = campaignCode !== campaignCodeDetails?.campaignCode;

    if (needsDataFetch || (campaignCode && campaignCodeChanged)) {
      dispatch(becomeAMemberActions.getBecomeAMember.request(campaignCode || undefined));
    }
  }, [dispatch, hasBecomeAMemberProducts, queryParams, campaignCodeDetails?.campaignCode]);

  const {
    customerInformation,
    customerInformation: {
      meta: { fetchState },
    },
  } = useSelector((state) => state.myMembership);

  useEffect(() => {
    if (richToken && fetchState === 'initial') {
      dispatch(actions.getCustomerInformation.request(richToken));
    }
  }, [richToken, dispatch, fetchState]);

  useEffect(() => {
    if (
      customerInformation?.data?.membership != null &&
      customerInformation?.data?.membership?.status === CustomerMembershipStatus.ActiveMembership &&
      fetchState === 'success'
    ) {
      navigate(`/${MEMBERPAGES_URL}/mitt-medlemskap`, { replace: true });
    }
  }, [customerInformation, navigate, fetchState]);

  const steps: Step[] = [
    { step: Members, name: 'Om meg', type: 'FORM' },
    ...(product && product?.availableAddOnProducts
      ? [{ step: Addons, name: 'Tilleggsvalg', type: 'FORM' } as Step]
      : []),
    { step: Consents, name: 'Samtykker', type: 'FORM' },
    { step: 'Betaling', name: 'Betaling', type: 'REDIRECT' },
  ];

  const sendVirtualPageView = (activeStep: number) => {
    const pageUrl = `${baseUrl}${window.location.pathname}${window.location.search}`;
    const pageReferrer = getPageReferrer();

    dataLayer?.push({
      event: 'virtualPageView',
      page: window.location.pathname,
      pageUrl,
      pageTitle: documentTitle(activeStep, steps),
      ...(pageReferrer ? { page_referrer: pageReferrer } : {}),
      ...(campaignCodeDetails?.campaignCode && { campaign_code: campaignCodeDetails.campaignCode }),
    });
    setLastVirtualPageUrl(pageUrl);
  };

  const handleStepChange = ({ activeStep }: ActiveStep) => {
    window.scrollTo(0, 0);
    if (activeStep < steps.length) {
      updateUrlQueryValues.updateWizardStep(activeStep.toFixed(0));
    }
    sendVirtualPageView(activeStep);
  };

  return (
    <LayoutWithMainContent
      title="Bli Medlem"
      description="Her kan du bli NAF-medlem"
      url={windowPath}
      gtmArgs={{
        page_type: 'Bli Medlem',
        pageCategory: '',
        contentId: 'become-a-member',
      }}
      internalSearchMetaData={{
        cardType: InternalSearchCardType.BecomeAMember,
      }}
      isHiddenFromSearch
    >
      {initialWizardStep != null && fetchState !== 'loading' && hasInitializedMembershipProduct ? (
        <Grid className="Wrapper">
          <GridRow className="GridRow">
            <GridCol s="12" m="12" l="12" xl="12">
              <S.Heading variant={TextVariant.Header1}>Bli medlem</S.Heading>
            </GridCol>
          </GridRow>
          <GridRow>
            <GridCol s="12" m="12" l="12" xl="12">
              <StepWizard
                transitions={{}}
                initialStep={initialWizardStep ?? 1}
                isLazyMount
                onStepChange={handleStepChange}
                nav={<Progressbar steps={steps} />}
              >
                <Members profile={profile} profileIsLoaded={profileIsLoaded} setProfileIsLoaded={setProfileIsLoaded} />
                {product && product.availableAddOnProducts && <Addons />}
                <Consents />
                <Redirect />
              </StepWizard>
            </GridCol>
          </GridRow>
        </Grid>
      ) : (
        <LoaderContent />
      )}
    </LayoutWithMainContent>
  );
};
